import React from "react";

// ------------------------------------------------------------------
// Trauma-Informed Navigation Icons
// ------------------------------------------------------------------
// Custom SVG icons designed to feel safe, supportive, and calming.
// All icons share the same visual language:
//   – 2px stroke width (bold enough for 20px display)
//   – rounded stroke-linecap / stroke-linejoin
//   – 24 × 24 viewBox with generous padding
//   – currentColor stroke so they theme automatically
//   – maximum 3-4 path elements for clarity at small sizes
// ------------------------------------------------------------------

const iconProps = {
  xmlns: "http://www.w3.org/2000/svg",
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2.25,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

/** 🏡 Safe Haven — Home
 *  A welcoming house with a heart centred in the doorway,
 *  conveying safety, belonging, and grounding. */
export const SafeHavenIcon = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>(
  (props, ref) => (
    <svg ref={ref} {...iconProps} {...props}>
      {/* House outline */}
      <path d="M4 10 L12 4 L20 10" />
      <path d="M5 10 V19 H10 V14 H14 V19 H19 V10" />
      {/* Heart in the doorway */}
      <path d="M12 11.5 C12 11.5 10.5 10 9.5 10 A1.5 1.5 0 0 0 8 11.5 C8 13 12 16 12 16 C12 16 16 13 16 11.5 A1.5 1.5 0 0 0 14.5 10 C13.5 10 12 11.5 12 11.5Z" />
    </svg>
  ),
);
SafeHavenIcon.displayName = "SafeHavenIcon";

/** 📖 Open Reflection Journal — Journal
 *  A simple open book with a few bold horizontal lines
 *  on each page suggesting writing and self-reflection. */
export const ReflectionJournalIcon = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>(
  (props, ref) => (
    <svg ref={ref} {...iconProps} {...props}>
      {/* Left page */}
      <path d="M3 6 C3 6 6 5 8 6.5 V17.5 C6 16 3 16.5 3 16.5 V6Z" />
      {/* Right page */}
      <path d="M21 6 C21 6 18 5 16 6.5 V17.5 C18 16 21 16.5 21 16.5 V6Z" />
      {/* Spine curve */}
      <path d="M8 6.5 C10 5.5 14 5.5 16 6.5" />
      {/* Text lines */}
      <path d="M5 9.5 H7" />
      <path d="M5 12.5 H6.5" />
      <path d="M17 9.5 H19" />
      <path d="M17.5 12.5 H19" />
    </svg>
  ),
);
ReflectionJournalIcon.displayName = "ReflectionJournalIcon";

/** 🌿 Gentle Support — My Recovery Journey
 *  Two curved open hands cradling a small heart,
 *  symbolising practical care, nurturing tools, and support. */
export const GentleSupportIcon = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>(
  (props, ref) => (
    <svg ref={ref} {...iconProps} {...props}>
      {/* Left hand */}
      <path d="M5 14 C3.5 13 3 11.5 4 10 C5 9 6.5 9.5 7 10.5 L8 12.5" />
      <path d="M8 12.5 C7 14.5 7 16 8.5 17.5" />
      {/* Right hand */}
      <path d="M19 14 C20.5 13 21 11.5 20 10 C19 9 17.5 9.5 17 10.5 L16 12.5" />
      <path d="M16 12.5 C17 14.5 17 16 15.5 17.5" />
      {/* Heart in the centre */}
      <path d="M12 10 C12 10 10.5 8.5 9.5 8.5 A1.5 1.5 0 0 0 8 10 C8 11.5 12 14.5 12 14.5 C12 14.5 16 11.5 16 10 A1.5 1.5 0 0 0 14.5 8.5 C13.5 8.5 12 10 12 10Z" />
    </svg>
  ),
);
GentleSupportIcon.displayName = "GentleSupportIcon";

/** 📚 Learning & Growth — Library
 *  An open book with a small sprout rising from the spine,
 *  representing education that leads to growth and recovery. */
export const LearningGrowthIcon = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>(
  (props, ref) => (
    <svg ref={ref} {...iconProps} {...props}>
      {/* Book left page */}
      <path d="M3 6 C3 6 6 5 8 6.5 V17.5 C6 16 3 16.5 3 16.5 V6Z" />
      {/* Book right page */}
      <path d="M21 6 C21 6 18 5 16 6.5 V17.5 C18 16 21 16.5 21 16.5 V6Z" />
      {/* Spine */}
      <path d="M8 6.5 C10 5.5 14 5.5 16 6.5" />
      {/* Sprout */}
      <path d="M12 4.5 C12 4.5 10.5 3 10.5 2 A1.5 1.5 0 0 1 13.5 2 C13.5 3 12 4.5 12 4.5Z" />
      <path d="M12 4.5 V7" />
    </svg>
  ),
);
LearningGrowthIcon.displayName = "LearningGrowthIcon";

/** 🤍 Support Circle — Community
 *  Three gentle figures arranged in a warm circle with a
 *  shared heart at the centre, conveying belonging and connection. */
export const SupportCircleIcon = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>(
  (props, ref) => (
    <svg ref={ref} {...iconProps} {...props}>
      {/* Three people */}
      <circle cx="8" cy="7" r="2.2" />
      <path d="M5.5 16.5 C5.5 14 6.5 12.5 8 12.5 C9.5 12.5 10.5 14 10.5 16.5" />
      <circle cx="16" cy="7" r="2.2" />
      <path d="M13.5 16.5 C13.5 14 14.5 12.5 16 12.5 C17.5 12.5 18.5 14 18.5 16.5" />
      {/* Central heart */}
      <path d="M12 8.5 C12 8.5 10.5 7 9.5 7 A1.5 1.5 0 0 0 8 8.5 C8 10 12 13 12 13 C12 13 16 10 16 8.5 A1.5 1.5 0 0 0 14.5 7 C13.5 7 12 8.5 12 8.5Z" />
    </svg>
  ),
);
SupportCircleIcon.displayName = "SupportCircleIcon";

/** ✨ Live Gathering — Events
 *  A warm circle containing a sparkle and two small accent stars,
 *  representing a welcoming live workshop or gathering. */
export const LiveGatheringIcon = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>(
  (props, ref) => (
    <svg ref={ref} {...iconProps} {...props}>
      {/* Outer circle */}
      <circle cx="12" cy="12" r="7.5" />
      {/* Main sparkle */}
      <path d="M12 7 L12.9 10.1 L16 11 L12.9 11.9 L12 15 L11.1 11.9 L8 11 L11.1 10.1 Z" />
      {/* Small accent sparkles */}
      <path d="M17.5 5.5 L17.8 6.5 L18.5 6.8 L17.8 7.1 L17.5 8 L17.2 7.1 L16.5 6.8 L17.2 6.5 Z" />
      <path d="M6.5 16 L6.8 17 L7.5 17.2 L6.8 17.5 L6.5 18.5 L6.2 17.5 L5.5 17.2 L6.2 17 Z" />
    </svg>
  ),
);
LiveGatheringIcon.displayName = "LiveGatheringIcon";

/** 🤝 Professional Support — Find a Therapist
 *  Two figures side by side with one extending an open hand to the other,
 *  symbolising compassionate, licensed professional care. */
export const ProfessionalSupportIcon = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>(
  (props, ref) => (
    <svg ref={ref} {...iconProps} {...props}>
      {/* Left figure (therapist) */}
      <circle cx="8" cy="7" r="2.2" />
      <path d="M5 17 C5 14.5 6 13 8 13 C10 13 11 14.5 11 17" />
      {/* Right figure (client) */}
      <circle cx="16" cy="8" r="1.8" />
      <path d="M14 17 C14 15 14.8 13.7 16 13.7 C17.2 13.7 18 15 18 17" />
      {/* Open hand offered between them */}
      <path d="M11.5 11.5 C12.3 11 13.5 11 14.3 11.7" />
    </svg>
  ),
);
ProfessionalSupportIcon.displayName = "ProfessionalSupportIcon";

/** 🌿 My Recovery Journey — Profile
 *  A winding path leading up to a small heart waypoint,
 *  representing personal progress on the recovery journey. */
export const MyJourneyIcon = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>(
  (props, ref) => (
    <svg ref={ref} {...iconProps} {...props}>
      {/* Winding path */}
      <path d="M5 20 C5 17 8 16 8 13 C8 10 5 9 5 6" />
      <path d="M5 6 C7 5.5 9 6 10 7.5" />
      {/* Heart waypoint at the top */}
      <path d="M16 6.5 C16 6.5 14.8 5.3 14 5.3 A1.3 1.3 0 0 0 12.7 6.5 C12.7 7.8 16 10 16 10 C16 10 19.3 7.8 19.3 6.5 A1.3 1.3 0 0 0 18 5.3 C17.2 5.3 16 6.5 16 6.5Z" />
      {/* Small milestone dot mid-path */}
      <circle cx="7" cy="13" r="0.8" />
    </svg>
  ),
);
MyJourneyIcon.displayName = "MyJourneyIcon";


// ------------------------------------------------------------------
// Unified icon map for easy consumption in nav components
// ------------------------------------------------------------------

export const NAV_ICONS = {
  home: SafeHavenIcon,
  journal: ReflectionJournalIcon,
  toolkit: GentleSupportIcon,
  library: LearningGrowthIcon,
  therapists: ProfessionalSupportIcon,
  community: SupportCircleIcon,
  events: LiveGatheringIcon,
  profile: MyJourneyIcon,
} as const;

export type NavIconKey = keyof typeof NAV_ICONS;
